<script setup lang="ts">
import { computed, ref } from 'vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps<{
    effect: string
    filterFlagId: number | null
    filterFlags: { id: number, value: string }[]
}>();
const emit = defineEmits<{
    'effect-change': [change: { effect: string, filterFlagId: number | null }]
}>();
type Operation = 'none' | 'set' | 'reset' | 'toggle';
type Target = 'present' | number | null;

const [initialOperation, initialTarget] = parseEffect();
const operation = ref<Operation>(initialOperation);
const target = ref<Target>(initialTarget);

const hasTarget = computed(() => operation.value !== 'none');

function parseEffect(): [Operation, Target] {
    if (props.effect === 'none') {
        return ['none', null];
    }

    if (props.effect.endsWith('-present')) {
        return [props.effect.replace('-present', '') as Operation, 'present'];
    }

    return [props.effect.replace('-filter-flag', '') as Operation, props.filterFlagId];
}

function emitIfComplete() {
    if (operation.value === 'none') {
        emit('effect-change', { effect: 'none', filterFlagId: null });

        return;
    }

    if (target.value === 'present') {
        emit('effect-change', { effect: `${operation.value}-present`, filterFlagId: null });

        return;
    }

    if (target.value !== null) {
        emit('effect-change', { effect: `${operation.value}-filter-flag`, filterFlagId: target.value });
    }
}
</script>

<template>
    <div class="flex flex-col gap-2">
        <select
            v-model="operation"
            class="kgr-select"
            @change="emitIfComplete"
        >
            <option value="none">
                {{ $t('Keine Auswirkung') }}
            </option>
            <option value="set">
                {{ $t('Setzen') }}
            </option>
            <option value="reset">
                {{ $t('Zurücksetzen') }}
            </option>
            <option value="toggle">
                {{ $t('Umschalten') }}
            </option>
        </select>
        <select
            v-if="hasTarget"
            v-model="target"
            class="kgr-select"
            @change="emitIfComplete"
        >
            <option :value="null">
                {{ $t('pleaseChooseAnOption') }}
            </option>
            <option value="present">
                {{ $t('Anwesenheit') }}
            </option>
            <option
                v-for="filterFlag in filterFlags"
                :key="filterFlag.id"
                :value="filterFlag.id"
            >
                {{ filterFlag.value }}
            </option>
        </select>
    </div>
</template>
